import { useState } from "react";
import IntranetLayout from "@/components/IntranetLayout";
import ModuleHeader from "@/components/voff/ModuleHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Anchor, Download, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export const PreArrivalChecklistContent = () => {
  const [generating, setGenerating] = useState(false);
  const [checklist, setChecklist] = useState<any>(null);
  const [form, setForm] = useState({ vessel: "Velocity Vanguard", from: "Georgetown", to: "Liza Destiny FPSO", eta: "", crew: "", cargo: "", client: "ExxonMobil Guyana" });

  const generate = () => {
    setGenerating(true);
    setTimeout(() => {
      setChecklist({
        vessel: form.vessel, voyage: `${form.from} → ${form.to}`,
        sections: [
          { name: "Bridge & Navigation", items: [
            "ECDIS chart route loaded and reviewed by Master",
            "GPS/DGPS reference signals verified — both channels operational",
            "AIS data updated with destination, ETA, draft",
            "VHF Ch 13/16 manned 30nm prior to arrival",
            "Pilot transfer arrangements confirmed (if applicable)",
            "Anchor ready for letting go (forward bay)",
          ]},
          { name: "Engine Room", items: [
            "Both main engines on standby 1hr prior to arrival",
            "Bow thruster tested and operational",
            "Lift fans confirmed running at rated parameters",
            "Bilge alarms tested — all dry",
            "Fuel tank soundings logged",
          ]},
          { name: "DP & Approach", items: [
            "K-Pos DP system pre-arrival check complete (both controllers)",
            "DP capability plot for prevailing weather verified",
            "ASOG (Activity Specific Operating Guidelines) reviewed against forecast",
            "500m zone entry permission obtained from FPSO OIM",
            "Stand-off position confirmed (typically 1500m initial approach)",
          ]},
          { name: "Safety", items: [
            "All passengers briefed and PFDs donned",
            "Liferafts checked and ready for launch",
            "Fire detection system armed",
            "Emergency stop tested at bridge wing",
            "Helideck status (if FPSO has hot work) — confirmed clear",
          ]},
          { name: "Communications & Documentation", items: [
            `Pre-arrival info to ${form.client} OIM transmitted (vessel ID, POB, fuel ROB, last port)`,
            "ISPS pre-arrival 24hr notification sent (ISPS Reg.4)",
            "Crew/passenger manifest with passport details ready",
            "Inward declaration prepared",
            "Cargo documentation reviewed",
          ]},
        ],
        warnings: [
          "Forecast Hs 1.8m — within SES limit but degraded transfer comfort; brief passengers",
          "Last DP trial >330 days — Annual DP Trial due within 35 days",
        ],
      });
      setGenerating(false);
      toast.success("Pre-arrival checklist generated");
    }, 1500);
  };

  return (
    <>
      <ModuleHeader title="Pre-Arrival Checklist" subtitle="Voyage Approach & FPSO Pre-Arrival Generator" accent="lime" />

      <div className="grid lg:grid-cols-5 gap-6">
        <Card className="lg:col-span-2 border-[#D0DDE6]">
          <CardContent className="p-6 space-y-3">
            <h3 className="font-bold text-[#00283C]">Voyage Details</h3>
            <div><Label className="text-xs">Vessel</Label><Select value={form.vessel} onValueChange={(v) => setForm({...form, vessel: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["Velocity Vanguard","Velocity Cobalto","Velocity Paladio","Velocity Lerwick"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs">From</Label><Input value={form.from} onChange={(e) => setForm({...form, from: e.target.value})} /></div><div><Label className="text-xs">To</Label><Input value={form.to} onChange={(e) => setForm({...form, to: e.target.value})} /></div></div>
            <div><Label className="text-xs">ETA</Label><Input type="datetime-local" value={form.eta} onChange={(e) => setForm({...form, eta: e.target.value})} /></div>
            <div className="grid grid-cols-2 gap-3"><div><Label className="text-xs">POB</Label><Input value={form.crew} onChange={(e) => setForm({...form, crew: e.target.value})} placeholder="80" /></div><div><Label className="text-xs">Cargo</Label><Input value={form.cargo} onChange={(e) => setForm({...form, cargo: e.target.value})} placeholder="2T light" /></div></div>
            <div><Label className="text-xs">Client</Label><Select value={form.client} onValueChange={(v) => setForm({...form, client: v})}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{["ExxonMobil Guyana","Azule Energy","TotalEnergies","BP Worldwide"].map(x => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent></Select></div>
            <Button onClick={generate} disabled={generating} className="w-full bg-[#8CB43C] hover:bg-[#8CB43C]/90 text-white">
              {generating ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : "Generate Checklist"}
            </Button>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3 border-[#D0DDE6]">
          <CardContent className="p-6">
            {!checklist ? (
              <div className="text-center py-20 text-muted-foreground"><Anchor className="w-16 h-16 mx-auto mb-4 text-[#288CC8]/40" /><p>Configure voyage to generate a pre-arrival checklist</p></div>
            ) : (
              <div className="space-y-4">
                <div className="border-b border-[#D0DDE6] pb-3">
                  <div className="text-xs uppercase text-[#6B8A9A]">Pre-Arrival Checklist</div>
                  <div className="text-lg font-bold text-[#00283C]">{checklist.vessel}</div>
                  <div className="text-sm">{checklist.voyage}</div>
                </div>
                {checklist.warnings.length > 0 && (
                  <div className="bg-[#E8A020]/10 border border-[#E8A020]/40 rounded p-3 text-xs">
                    <div className="font-bold text-[#E8A020] mb-1">⚠ Conditions of note:</div>
                    {checklist.warnings.map((w: string) => <div key={w}>• {w}</div>)}
                  </div>
                )}
                {checklist.sections.map((s: any) => (
                  <div key={s.name}>
                    <div className="font-bold text-sm text-[#288CC8] mb-2 uppercase tracking-wider">{s.name}</div>
                    <ul className="space-y-1.5">{s.items.map((i: string) => (
                      <li key={i} className="text-sm flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-[#2CA048] mt-0.5 shrink-0" />{i}</li>
                    ))}</ul>
                  </div>
                ))}
                <div className="flex gap-2 pt-3 border-t border-[#D0DDE6]"><Button onClick={() => toast.success("Generating: PDF", { description: "Your file will download shortly." })} size="sm" variant="outline"><Download className="w-3 h-3 mr-1" />PDF</Button><Button onClick={() => toast.success("Sent: Email to Master", { description: "Recipients will be notified." })} size="sm" variant="outline">Email to Master</Button></div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
};

const PreArrivalChecklist = () => (
  <IntranetLayout>
    <PreArrivalChecklistContent />
  </IntranetLayout>
);
export default PreArrivalChecklist;
